<app-page
  [title]="'License Renewal - ' + (licenseStore.activeReview()?.reviewNumber || 'Loading...')"
  [showBackButton]="true"
  [useWorkflowNavigation]="true"
  [backRoute]="['/certification/licenses/extension-reviews']">
  <!-- Action Buttons -->
  <div actionButtons>
    <p-button
      (onClick)="openTimeline()"
      severity="secondary"
      pTooltip="View timeline and notes"
      tooltipPosition="top"
      [text]="true"
      class="mr-2"
      data-testid="timeline-button">
      <fa-icon [icon]="['fas', 'history']" size="lg"></fa-icon>
    </p-button>

    @if (actionMenuItems().length > 0) {
      <app-actions-menu [items]="actionMenuItems()" testId="review-actions-menu"> </app-actions-menu>
    }
  </div>

  <!-- Loading State -->
  @if (licenseStore.activeReviewLoading()) {
    <div class="flex justify-content-center align-items-center p-5">
      <i class="pi pi-spin pi-spinner text-4xl"></i>
    </div>
  }

  <!-- Error State -->
  @if (licenseStore.activeReviewError()) {
    <p-message severity="error">{{ licenseStore.activeReviewError()! }}</p-message>
  }

  <!-- Review Content -->
  @if (!licenseStore.activeReviewLoading() && licenseStore.activeReview()) {
    <div class="workflow-container">
      <div class="grid grid-nogutter border-top-1 surface-border">
        <!-- Left Column: Review Information -->
        <div class="col-12 md:col-5 pt-4 pb-4">
          <div class="flex justify-content-between align-items-center mb-3">
            <h3>Review Information</h3>
          </div>

          <!-- Review Details -->
          <div class="p-2">
            <div class="text-400 text-sm mb-1">Review Number</div>
            <div class="text-900 font-medium">
              <b>{{ licenseStore.activeReview()!.reviewNumber }}</b>
            </div>
          </div>

          <div class="p-2">
            <div class="text-400 text-sm mb-1">Certification Year</div>
            <div class="text-900 font-medium">{{ licenseStore.activeReview()!.certificationYear }}</div>
          </div>

          <div class="p-2">
            <div class="text-400 text-sm mb-1">Current Status</div>
            <div class="text-900">
              <p-tag
                [severity]="getStateSeverity(licenseStore.activeReview()!.currentState)"
                [value]="getStateLabel(licenseStore.activeReview()!.currentState)">
              </p-tag>
            </div>
          </div>

          @if (licenseStore.activeReview()!.proposedExpiryDate) {
            <div class="p-2">
              <div class="text-400 text-sm mb-1">Proposed Expiry Date</div>
              <div class="text-900 font-medium">
                {{ licenseStore.activeReview()!.proposedExpiryDate | date: 'mediumDate' : 'UTC' }}
              </div>
            </div>
          }

          @if (licenseStore.activeReview()!.selectedCandidatesCount > 0) {
            <div class="p-2">
              <div class="text-400 text-sm mb-1">Selected Candidates</div>
              <div class="text-900 font-medium">{{ licenseStore.activeReview()!.selectedCandidatesCount }}</div>
            </div>
          }

          @if (licenseStore.activeReview()!.extendedLicensesCount > 0) {
            <div class="p-2">
              <div class="text-400 text-sm mb-1">Renewed Licenses</div>
              <div class="text-900 font-medium text-green-600">
                {{ licenseStore.activeReview()!.extendedLicensesCount }}
              </div>
            </div>
          }

          @if (licenseStore.activeReview()!.completedOn) {
            <div class="p-2 mt-3">
              <div class="text-400 text-sm mb-1">Completed On</div>
              <div class="text-900 font-medium">{{ licenseStore.activeReview()!.completedOn | date: 'medium' }}</div>
            </div>
          }

          <!-- Processed Licenses Summary (FinalReview and Completed states) -->
          @if (showProcessedLicenses()) {
            <div class="mt-4">
              <p-divider></p-divider>
              <div class="flex justify-content-between align-items-center mb-3">
                <h3>Processed Licenses</h3>
                @if (hasProcessingCertificates()) {
                  <fa-icon [icon]="['fas', 'spinner']" [animation]="'spin'" class="text-primary"></fa-icon>
                }
              </div>

              @if (licenseStore.selectedCandidatesLoading()) {
                <div class="flex justify-content-center p-4">
                  <i class="pi pi-spin pi-spinner text-2xl"></i>
                </div>
              } @else {
                <app-processed-licenses-cards
                  [licenses]="processedLicensesForDisplay()"
                  [canRetry]="canRetryFailures()"
                  [isRefreshing]="false"
                  (retryCertificate)="onRetryCertificate($event)"
                  (retryGlNotification)="onRetryGlNotification($event)"
                  (viewCertificate)="onViewCertificate($event)">
                </app-processed-licenses-cards>
              }
            </div>
          }
        </div>

        <!-- Right Column: Workflow Steps -->
        <div class="col-12 md:col-7 pl-2 pt-4 pb-4">
          <div class="flex justify-content-between align-items-center mb-3">
            <h3>Workflow</h3>
          </div>

          <!-- Stepper for workflow steps -->
          <p-stepper
            #stepperRef
            [value]="activeIndex() + 1"
            [linear]="!isUserSuperAdmin(currentUser())"
            class="transition-all">
            <!-- Step 1: Candidates Selection -->
            <p-step-item [value]="1">
              <p-step>
                <span>Candidates Selection</span>
              </p-step>
              <p-step-panel class="overflow-visible">
                <ng-template #content>
                  <!-- Debug: Current state = {{ licenseStore.activeReview()!.currentState }} -->
                  <!-- Step 1: Candidates Selection (Created state) -->
                  @if (licenseStore.activeReview()!.currentState === 'Created') {
                    <div class="step-content">
                      <h3 class="mb-3">Step 1: Candidates Selection</h3>
                      <p class="text-600 mb-4">
                        Search and identify candidates for license renewal for the year
                        {{ licenseStore.activeReview()!.certificationYear }}. The system will find all licenses eligible
                        for renewal based on their inspection history.
                      </p>
                      <div class="flex justify-content-end mb-3">
                        @if (canSelectCandidates()) {
                          <p-button
                            label="Start selecting candidates..."
                            icon="pi pi-search"
                            [loading]="licenseStore.availableCandidatesLoading()"
                            (onClick)="selectCandidates()">
                          </p-button>
                        }
                      </div>
                      <!-- Show empty table while waiting for identification -->
                      <p-table
                        [value]="[]"
                        [paginator]="true"
                        [rows]="10"
                        [rowsPerPageOptions]="[10, 25, 50]"
                        dataKey="id"
                        styleClass="p-datatable-striped">
                        <ng-template #header>
                          <tr>
                            <th style="width: 3rem">Select</th>
                            <th>License #</th>
                            <th>Coater</th>
                            <th>Current Expiry</th>
                            <th>Status</th>
                            <th>Inspections<br />(Current Year)</th>
                            <th>Actions</th>
                          </tr>
                        </ng-template>
                        <ng-template #emptymessage>
                          <tr>
                            <td colspan="7" class="text-center p-4">
                              Click "Select Candidates" to search for licenses eligible for renewal.
                            </td>
                          </tr>
                        </ng-template>
                      </p-table>
                    </div>
                  }

                  <!-- License Renewal (Review state) -->
                  @if (licenseStore.activeReview()!.currentState !== 'Created') {
                    <div class="step-content">
                      <h3 class="mb-3">License Renewal</h3>
                      <p class="text-600 mb-3">
                        Review the selected candidates for license renewal. You can review and manually add individual
                        licenses, review inspection details by clicking on the inspection results or proceed with all
                        eligible candidates.
                      </p>
                      <div class="flex justify-content-between align-items-center mb-3">
                        <div>
                          <p-chip [label]="'Total: ' + licenseStore.availableCandidates().length" styleClass="mr-2">
                          </p-chip>
                          <p-chip [label]="'Selected: ' + licenseStore.selectedCandidateIds().length" styleClass="mr-2">
                          </p-chip>
                          <p-chip [label]="'Eligible: ' + eligibleCandidatesCount()" icon="pi pi-check"> </p-chip>
                        </div>
                        @if (canUpdateSelection()) {
                          <div>
                            <p-button
                              icon="pi pi-replay"
                              severity="secondary"
                              size="small"
                              [loading]="licenseStore.availableCandidatesLoading()"
                              (onClick)="selectCandidates()"
                              styleClass="mr-2"
                              pTooltip="Re-select Candidates"
                              tooltipPosition="bottom">
                            </p-button>
                            <p-button
                              icon="pi pi-check-square"
                              severity="secondary"
                              size="small"
                              (onClick)="selectAllEligible()"
                              styleClass="mr-2"
                              pTooltip="Select All Eligible"
                              tooltipPosition="bottom"></p-button>
                            <p-button
                              icon="pi pi-times"
                              severity="secondary"
                              size="small"
                              (onClick)="clearSelection()"
                              styleClass="mr-2"
                              pTooltip="Clear Selection"
                              tooltipPosition="bottom"></p-button>
                            @if (licenseStore.unsavedChanges()) {
                              <p-button
                                icon="pi pi-save"
                                severity="warn"
                                size="small"
                                [loading]="licenseStore.availableCandidatesLoading()"
                                (onClick)="saveSelection()"
                                pTooltip="Save Selection"
                                tooltipPosition="bottom">
                              </p-button>
                            }
                          </div>
                        }
                      </div>
                      <!-- Candidates Table Header with Refresh Button -->
                      <div class="flex align-items-center gap-3 mb-3">
                        <h4 class="m-0">Candidates</h4>
                        @if (
                          licenseStore.availableCandidates().length > 0 && !licenseStore.availableCandidatesLoading()
                        ) {
                          <p-button
                            icon="pi pi-refresh"
                            label="Refresh Inspections"
                            [text]="true"
                            size="small"
                            [loading]="licenseStore.refreshingInspections()"
                            [disabled]="licenseStore.availableCandidatesLoading()"
                            (onClick)="refreshInspections()"
                            pTooltip="Refresh inspection data for all candidates"
                            tooltipPosition="bottom">
                          </p-button>
                        }
                      </div>
                      <p-message severity="info" class="mb-3">
                        <span>
                          Licenses in granting state are excluded from the renewal process. Please activate granting
                          licenses manually before including them in a renewal workflow.
                        </span>
                      </p-message>
                      <!-- Candidates Table -->
                      @if (licenseStore.availableCandidatesLoading()) {
                        <div class="flex justify-content-center p-4">
                          <i class="pi pi-spin pi-spinner text-2xl"></i>
                        </div>
                      }
                      <!-- No candidates found message -->
                      @if (
                        !licenseStore.availableCandidatesLoading() && licenseStore.availableCandidates().length === 0
                      ) {
                        <div>
                          <p-message severity="info" class="mb-3">
                            <span>
                              No candidates were found for the certification year
                              {{ licenseStore.activeReview()!.certificationYear }}. You can re-identify candidates using
                              the button above or proceed to confirm with no selections.
                            </span>
                          </p-message>
                          <p-table [value]="[]" [paginator]="false" dataKey="id" styleClass="p-datatable-striped">
                            <ng-template #header>
                              <tr>
                                <th style="width: 3rem">Select</th>
                                <th>License #</th>
                                <th>Coater</th>
                                <th>GL</th>
                                <th>Current Expiry</th>
                                <th>Status</th>
                                <th>Inspections<br />(Current Year)</th>
                              </tr>
                            </ng-template>
                            <ng-template #emptymessage>
                              <tr>
                                <td colspan="7" class="text-center p-4">No candidates were found.</td>
                              </tr>
                            </ng-template>
                          </p-table>
                        </div>
                      }
                      <!-- Regular candidates table -->
                      @if (
                        !licenseStore.availableCandidatesLoading() && licenseStore.availableCandidates().length > 0
                      ) {
                        <div>
                          <p-table
                            #candidatesTable
                            [value]="licenseStore.availableCandidates()"
                            [paginator]="true"
                            [rows]="10"
                            [rowsPerPageOptions]="[10, 25, 50]"
                            dataKey="id"
                            styleClass="p-datatable-striped"
                            (onPage)="onCandidatesPageChange($event)">
                            <ng-template #header>
                              <tr>
                                <th style="width: 3rem">
                                  @if (canUpdateSelection()) {
                                    <p-checkbox
                                      [binary]="true"
                                      [ngModel]="areAllCandidatesOnPageSelected()"
                                      (onChange)="toggleAllCandidatesOnPage($event)">
                                    </p-checkbox>
                                  }
                                </th>
                                <th>License #</th>
                                <th>Coater</th>
                                <th>GL</th>
                                <th>Current Expiry</th>
                                <th>Status</th>
                                <th>Inspections<br />(Current Year)</th>
                              </tr>
                            </ng-template>
                            <ng-template #body let-candidate>
                              <tr>
                                <td>
                                  @if (canUpdateSelection()) {
                                    <p-checkbox
                                      [binary]="true"
                                      [ngModel]="isCandidateSelected(candidate)"
                                      (onChange)="toggleCandidateSelection(candidate)">
                                    </p-checkbox>
                                  }
                                  @if (!canUpdateSelection() && isCandidateSelected(candidate)) {
                                    <i class="pi pi-check"></i>
                                  }
                                </td>
                                <td class="font-semibold">{{ candidate.license?.completeLicenseNumber }}</td>
                                <td>
                                  <div
                                    [pTooltip]="
                                      $safeNavigationMigration(candidate.license?.productionSite?.customer?.name)
                                    "
                                    [tooltipDisabled]="
                                      (candidate.license?.productionSite?.customer?.name?.length || 0) <= 32
                                    ">
                                    {{ candidate.license?.productionSite?.customer?.name || '' | slice: 0 : 50
                                    }}{{
                                      (candidate.license?.productionSite?.customer?.name?.length || 0) > 50 ? '...' : ''
                                    }}
                                  </div>
                                  <small class="text-500">{{ candidate.license?.productionSite?.city }}</small>
                                </td>
                                <td>
                                  @if (candidate.license?.productionSite?.customer?.assignedGl; as gl) {
                                    <span [pTooltip]="gl.name" tooltipPosition="top">{{
                                      gl.abbreviations?.[0] || gl.name
                                    }}</span>
                                  } @else {
                                    <span class="text-400">-</span>
                                  }
                                </td>
                                <td>
                                  {{
                                    $safeNavigationMigration(candidate.license?.expiryDate) | date: 'shortDate' : 'UTC'
                                  }}
                                </td>
                                <td>
                                  <p-tag
                                    [severity]="getEligibilitySeverity(candidate.isEligible)"
                                    [value]="getEligibilityLabel(candidate.isEligible)">
                                  </p-tag>
                                  @if (candidate.license?.lineValidationRemarks?.length) {
                                    <i
                                      class="pi pi-exclamation-triangle text-orange-500 ml-2 cursor-help"
                                      [pTooltip]="candidate.license!.lineValidationRemarks!.join('\n')"
                                      tooltipPosition="top"></i>
                                  }
                                  @if (candidate.eligibilityReason) {
                                    <i
                                      class="pi pi-info-circle ml-2"
                                      [pTooltip]="candidate.eligibilityReason"
                                      tooltipPosition="top"></i>
                                  }
                                </td>
                                <td>
                                  <div class="flex gap-2 align-items-center flex-wrap">
                                    @let allInspections = getAllInspections(candidate);
                                    @if (allInspections.length > 0) {
                                      @for (inspection of allInspections; track inspection.id) {
                                        <a
                                          class="text-primary cursor-pointer hover:underline"
                                          (click)="navigateToInspection(inspection.id)"
                                          [pTooltip]="
                                            'View ' +
                                            formatInspectionType(inspection) +
                                            ' inspection' +
                                            (inspection.inspectionDate
                                              ? ' from ' + (inspection.inspectionDate | date: 'shortDate' : 'UTC')
                                              : '')
                                          ">
                                          {{ formatInspectionType(inspection) }}
                                          : {{ inspection.isSatisfactory ? '✓' : '✗' }}
                                        </a>
                                      }
                                    } @else {
                                      <span class="text-500">No inspections</span>
                                    }
                                  </div>
                                </td>
                              </tr>
                            </ng-template>
                            <ng-template #emptymessage>
                              <tr>
                                <td colspan="6" class="text-center p-4">No candidates found.</td>
                              </tr>
                            </ng-template>
                          </p-table>
                        </div>
                      }
                      <!-- Confirm Selection Button -->
                      @if (canConfirmSelection()) {
                        <div class="mt-4 flex justify-content-end">
                          <p-button
                            label="Confirm Selection and Proceed"
                            icon="pi pi-arrow-right"
                            (onClick)="openConfirmDialog()">
                          </p-button>
                        </div>
                      }
                    </div>
                  }
                </ng-template>
              </p-step-panel>
            </p-step-item>

            <!-- Step 2: Certificate Issuing -->
            <p-step-item [value]="2">
              <p-step>
                <span>GL Notification</span>
              </p-step>
              <p-step-panel class="overflow-visible">
                <ng-template #content>
                  <!-- GL Notification (IssuingCertificates state only) -->
                  <div class="step-content">
                    <h3 class="mb-3">Step 2: GL Notification</h3>
                    <p class="text-600 mb-3">
                      The renewed certificates for the renewed licenses are now available. Review the list below and
                      notify the respective GLs (General Licensees) about the availability of new certificates for their
                      coaters.
                    </p>

                    <!-- Renewal Summary -->
                    @if (licenseStore.activeReview()!.extendedLicensesCount > 0) {
                      <div class="surface-100 border-round p-3 mb-4">
                        <h4 class="mb-3">Renewal Summary</h4>
                        <div class="grid">
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Renewed Licenses</label>
                            <div class="text-green-600 text-2xl font-bold">
                              {{ licenseStore.activeReview()!.extendedLicensesCount }}
                            </div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">New Expiry Date</label>
                            <div class="text-900 text-xl font-semibold">
                              {{ licenseStore.activeReview()!.proposedExpiryDate | date: 'mediumDate' : 'UTC' }}
                            </div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Certificates Generation</label>
                            @if (licenseStore.certificateGenerationStatus(); as status) {
                              <div class="flex align-items-center gap-2">
                                <div class="text-900 text-xl font-semibold">
                                  {{ status.completedCount }} / {{ status.totalCandidates }}
                                </div>
                                @if (status.pendingCount > 0 || status.queuedCount > 0 || status.processingCount > 0) {
                                  <i class="pi pi-spin pi-spinner text-blue-500" title="Generating certificates..."></i>
                                }
                                @if (status.completedCount === status.totalCandidates && status.failedCount === 0) {
                                  <i class="pi pi-check-circle text-green-500" title="All completed"></i>
                                }
                                @if (status.failedCount > 0) {
                                  <i
                                    class="pi pi-exclamation-triangle text-red-500"
                                    [title]="status.failedCount + ' failed'"></i>
                                }
                              </div>
                            } @else {
                              <div class="text-500">Loading...</div>
                            }
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Renewed Certificates Table -->
                    <app-renewed-certificates-table
                      [certificates]="renewedCertificates()"
                      [enableSelection]="!licenseStore['glNotificationSent']()"
                      (selectionChanged)="onCertificateSelectionChanged($event)">
                    </app-renewed-certificates-table>

                    <!-- GL Notification Actions -->
                    @if (!licenseStore.glNotificationSent()) {
                      <div class="mt-4">
                        <p-message severity="info" class="mb-3">
                          <span>
                            Select the certificates above to notify the respective GLs. The GLs will be informed about
                            the availability of new certificates and can proceed with their own distribution process.
                          </span>
                        </p-message>

                        @if (canNotifyGLs()) {
                          <div class="flex justify-content-end mb-3">
                            <p-button
                              label="Skip"
                              [text]="true"
                              severity="secondary"
                              (onClick)="skipGLNotification()"
                              styleClass="mr-2"
                              pTooltip="Skip notifying GLs and proceed to finalize"
                              tooltipPosition="top">
                            </p-button>
                            <p-button
                              label="Notify GLs"
                              icon="pi pi-send"
                              [loading]="licenseStore.isExecuting()"
                              (onClick)="notifyGLs()"
                              [disabled]="selectedCertificates.length === 0"
                              styleClass="mr-2">
                            </p-button>
                          </div>
                        }
                      </div>
                    } @else if (licenseStore['glNotificationResult']()) {
                      <div class="surface-100 border-round p-3 mb-4">
                        <h4 class="mb-3">GLs Notified</h4>
                        <div class="grid">
                          <div class="col-12 md:col-6">
                            <label class="text-500 font-medium">GLs Notified</label>
                            <div class="text-green-600 text-xl font-bold">
                              {{ licenseStore['glNotificationResult']()!.glsNotified }}
                            </div>
                          </div>
                          <div class="col-12 md:col-6">
                            <label class="text-500 font-medium">Certificates Covered</label>
                            <div class="text-blue-600 text-xl font-bold">
                              {{ licenseStore['glNotificationResult']()!.certificatesCount }}
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Proceed to Finalize -->
                    @if (licenseStore['glNotificationSent']() || licenseStore['glNotificationResult']()) {
                      <p-divider></p-divider>
                      <div class="mt-4">
                        <p-message severity="info" class="mb-3">
                          <span>
                            GLs have been notified about the new certificates. Proceed to the next step to finalize the
                            review.
                          </span>
                        </p-message>
                      </div>
                    }
                  </div>
                </ng-template>
              </p-step-panel>
            </p-step-item>

            <!-- Step 3: Create RN1 Inspections -->
            <p-step-item [value]="3">
              <p-step>
                <span>Create RN1 Inspections</span>
              </p-step>
              <p-step-panel>
                <ng-template #content>
                  <div class="step-content">
                    <h3 class="mb-3">Step 3: Create RN1 Inspections for Next Year</h3>
                    <p class="text-600 mb-3">
                      Create RN1 inspections for the next certification year based on the most recent satisfactory
                      inspection for each renewed license.
                    </p>

                    <!-- Summary Card -->
                    @if (licenseStore.activeReview()!.extendedLicensesCount > 0) {
                      <div class="surface-100 border-round p-3 mb-4">
                        <h4 class="mb-3">RN1 Creation Summary</h4>
                        <div class="grid">
                          <div class="col-12 md:col-6">
                            <label class="text-500 font-medium">Licenses Ready for RN1</label>
                            <div class="text-blue-600 text-2xl font-bold">
                              {{ licenseStore.activeReview()!.extendedLicensesCount }}
                            </div>
                          </div>
                          <div class="col-12 md:col-6">
                            <label class="text-500 font-medium">Target Year</label>
                            <div class="text-900 text-xl font-semibold">
                              {{ licenseStore.activeReview()!.certificationYear + 1 }}
                            </div>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Before Creation -->
                    @if (!licenseStore.rn1CreationCompleted()) {
                      <p-message severity="info" class="mb-3">
                        <span>
                          Click below to automatically create RN1 inspections for the next certification year. The
                          system will use the most recent satisfactory inspection for each license.
                        </span>
                      </p-message>

                      @if (canCreateRn1Inspections()) {
                        <div class="flex justify-content-end gap-2 mb-3">
                          <p-button
                            label="Skip"
                            [text]="true"
                            severity="secondary"
                            (onClick)="skipRn1Creation()"
                            styleClass="mr-2"
                            pTooltip="Skip RN1 creation and proceed to finalize"
                            tooltipPosition="top">
                          </p-button>
                          <p-button
                            label="Create RN1 Inspections for Next Year"
                            icon="pi pi-plus-circle"
                            [loading]="licenseStore.rn1CreationInProgress()"
                            (onClick)="createRn1Inspections()">
                          </p-button>
                        </div>
                      }
                    }

                    <!-- After Creation - Results -->
                    @if (licenseStore.rn1CreationCompleted() && licenseStore.rn1CreationResult()) {
                      <div class="surface-100 border-round p-3 mb-4">
                        <h4 class="mb-3">RN1 Creation Results</h4>
                        <div class="grid">
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Successfully Created</label>
                            <div class="text-green-600 text-xl font-bold">
                              {{ licenseStore.rn1CreationResult()!.successCount }}
                            </div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Failed</label>
                            <div class="text-red-600 text-xl font-bold">
                              {{ licenseStore.rn1CreationResult()!.failureCount }}
                            </div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">New Inspections</label>
                            <div class="text-blue-600 text-xl font-bold">
                              {{ licenseStore.rn1CreationResult()!.createdInspectionIds.length }}
                            </div>
                          </div>
                        </div>

                        <!-- Show errors if any -->
                        @if (licenseStore.rn1CreationResult()!.errors.length > 0) {
                          <p-divider></p-divider>
                          <div class="mt-3">
                            <label class="text-500 font-medium mb-2 block">Errors</label>
                            @for (error of licenseStore.rn1CreationResult()!.errors; track $index) {
                              <p-message severity="error" class="mb-2">
                                <span>{{ error }}</span>
                              </p-message>
                            }
                          </div>
                        }
                      </div>

                      <p-message severity="success" class="mb-3">
                        <span> RN1 inspections have been created. You can now proceed to finalize the review. </span>
                      </p-message>
                    }
                  </div>
                </ng-template>
              </p-step-panel>
            </p-step-item>

            <!-- Step 4: Finalize Review -->
            <p-step-item [value]="4">
              <p-step>
                <span>Finalize Review</span>
              </p-step>
              <p-step-panel>
                <ng-template #content>
                  <!-- Step 4: Finalize Review (FinalReview state) -->
                  @if (licenseStore.activeReview()!.currentState === 'FinalReview') {
                    <div class="step-content">
                      <h3 class="mb-3">Step 4: Finalize Review</h3>
                      <p class="text-600 mb-3">
                        Certificates have been issued successfully. Add any process documentation notes to finalize this
                        License Renewal.
                      </p>
                      <!-- Summary of what was done -->
                      @if (licenseStore.activeReview()!.extendedLicensesCount > 0) {
                        <div class="surface-100 border-round p-3 mb-4">
                          <h4 class="mb-3">Review Summary</h4>
                          <div class="grid">
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">Renewed Licenses</label>
                              <div class="text-green-600 text-xl font-bold">
                                {{ licenseStore.activeReview()!.extendedLicensesCount }}
                              </div>
                            </div>
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">New Expiry Date</label>
                              <div class="text-900 font-semibold">
                                {{ licenseStore.activeReview()!.proposedExpiryDate | date: 'mediumDate' : 'UTC' }}
                              </div>
                            </div>
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">Certificates Sent</label>
                              <div class="text-blue-600 font-semibold">
                                <i class="pi pi-check-circle text-green-500"></i> Yes
                              </div>
                            </div>
                            @if (licenseStore.glNotificationResult()) {
                              <div class="col-12 md:col-3">
                                <label class="text-500 font-medium">GLs Notified</label>
                                <div class="font-semibold">
                                  @if (licenseStore.glNotificationResult()!.glsNotified === 0) {
                                    <span class="text-orange-600"> <i class="pi pi-forward"></i> Skipped </span>
                                  } @else {
                                    <span class="text-blue-600">
                                      <i class="pi pi-envelope text-blue-500"></i>
                                      {{ licenseStore.glNotificationResult()!.glsNotified }}
                                    </span>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                          @if (licenseStore.glNotificationResult()) {
                            <div class="mt-3 p-2 surface-50 border-round">
                              @if (licenseStore.glNotificationResult()!.glsNotified === 0) {
                                <i class="pi pi-forward text-orange-500 mr-2"></i>
                                <span class="text-600">
                                  GL notification was skipped. Certificates are ready for distribution.
                                </span>
                              } @else {
                                <i class="pi pi-info-circle text-blue-500 mr-2"></i>
                                <span class="text-600">
                                  General Licensees were notified about
                                  {{ licenseStore.glNotificationResult()!.certificatesCount }}
                                  new certificates
                                </span>
                              }
                            </div>
                          }
                        </div>
                      }
                      <!-- RN1 Inspections Summary -->
                      @if (licenseStore.rn1CreationResult()) {
                        <div class="surface-100 border-round p-3 mb-4">
                          <h4 class="mb-3">RN1 Inspections for Next Year</h4>
                          <div class="grid">
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">Successfully Created</label>
                              <div class="text-green-600 text-xl font-bold">
                                {{ licenseStore.rn1CreationResult()!.successCount }}
                              </div>
                            </div>
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">Failed</label>
                              <div class="font-semibold">
                                @if (licenseStore.rn1CreationResult()!.failureCount > 0) {
                                  <span class="text-red-600 text-xl font-bold">
                                    {{ licenseStore.rn1CreationResult()!.failureCount }}
                                  </span>
                                } @else {
                                  <span class="text-500"> <i class="pi pi-check-circle text-green-500"></i> 0 </span>
                                }
                              </div>
                            </div>
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">New Inspections</label>
                              <div class="text-blue-600 text-xl font-bold">
                                {{ licenseStore.rn1CreationResult()!.createdInspectionIds.length }}
                              </div>
                            </div>
                            <div class="col-12 md:col-3">
                              <label class="text-500 font-medium">Target Year</label>
                              <div class="text-900 font-semibold">
                                {{ licenseStore.activeReview()!.certificationYear + 1 }}
                              </div>
                            </div>
                          </div>
                          <!-- Error Messages Section -->
                          @if (licenseStore.rn1CreationResult()!.errors.length > 0) {
                            <div class="mt-3 p-3 surface-card border-round border-1 border-red-200">
                              <div class="flex align-items-center mb-2">
                                <i class="pi pi-exclamation-triangle text-red-500 mr-2"></i>
                                <span class="font-semibold text-900">
                                  Errors ({{ licenseStore.rn1CreationResult()!.errors.length }})
                                </span>
                              </div>
                              <div class="mt-2">
                                @for (error of licenseStore.rn1CreationResult()!.errors; track $index) {
                                  <div class="text-600 text-sm mb-1 pl-3">
                                    <i class="pi pi-circle-fill text-red-400 mr-2" style="font-size: 0.5rem"></i>
                                    {{ error }}
                                  </div>
                                }
                              </div>
                            </div>
                          } @else {
                            <div class="mt-3 p-2 surface-50 border-round">
                              <i class="pi pi-check-circle text-green-500 mr-2"></i>
                              <span class="text-600">
                                All RN1 inspections were created successfully for certification year
                                {{ licenseStore.activeReview()!.certificationYear + 1 }}
                              </span>
                            </div>
                          }
                        </div>
                      }
                      <div class="field mb-4">
                        <label for="finalNotes" class="block text-900 font-medium mb-2">
                          Process Documentation Notes
                        </label>
                        <textarea
                          pTextarea
                          id="finalNotes"
                          [(ngModel)]="finalReviewNotes"
                          rows="5"
                          class="w-full"
                          placeholder="Add any relevant notes about this review process...">
                        </textarea>
                        <small class="text-500">
                          These notes will be stored with the completed review for future reference.
                        </small>
                      </div>
                      @if (canComplete()) {
                        <div class="flex justify-content-end pr-2">
                          <p-button
                            label="Finalize Review"
                            icon="pi pi-check"
                            severity="success"
                            [loading]="licenseStore.activeReviewLoading()"
                            (onClick)="finalizeReview()">
                          </p-button>
                        </div>
                      }
                    </div>
                  }

                  @if (licenseStore.activeReview()!.currentState === 'Completed') {
                    <div class="step-content">
                      <h3 class="mb-3">Step 4: Review Finalized</h3>
                      <p-message severity="success" class="mb-3">
                        <span>
                          This License Renewal has been finalized successfully.
                          {{ licenseStore.activeReview()!.extendedLicensesCount }} licenses were renewed.
                        </span>
                      </p-message>
                      <div class="surface-100 border-round p-3">
                        <div class="grid">
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Review Number</label>
                            <div class="text-900 font-semibold">{{ licenseStore.activeReview()!.reviewNumber }}</div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Renewed Licenses</label>
                            <div class="text-900 font-semibold">
                              {{ licenseStore.activeReview()!.extendedLicensesCount }}
                            </div>
                          </div>
                          <div class="col-12 md:col-4">
                            <label class="text-500 font-medium">Completed On</label>
                            <div class="text-900">{{ licenseStore.activeReview()!.completedOn | date: 'medium' }}</div>
                          </div>
                        </div>
                        @if (licenseStore.activeReview()!.notes && licenseStore.activeReview()!.notes!.length > 0) {
                          <p-divider></p-divider>
                          <div>
                            <label class="text-500 font-medium">Review Notes</label>
                            <div class="mt-2">
                              @for (note of licenseStore.activeReview()!.notes; track note.id) {
                                <div class="mb-3 p-3 surface-50 border-round">
                                  <div class="flex align-items-center mb-2">
                                    @switch (note.type) {
                                      @case ('Approval') {
                                        <i class="pi pi-check-circle text-green-500 mr-2"></i>
                                        <span class="font-semibold text-green-700">Approval Note</span>
                                      }
                                      @case ('FinalReview') {
                                        <i class="pi pi-file-edit text-blue-500 mr-2"></i>
                                        <span class="font-semibold text-blue-700">Final Review Note</span>
                                      }
                                      @case ('Review') {
                                        <i class="pi pi-comment text-orange-500 mr-2"></i>
                                        <span class="font-semibold text-orange-700">Review Note</span>
                                      }
                                      @default {
                                        <i class="pi pi-info-circle text-gray-500 mr-2"></i>
                                        <span class="font-semibold text-gray-700">{{ note.type || 'Note' }}</span>
                                      }
                                    }
                                    @if (note.createdOn) {
                                      <span class="ml-auto text-400 text-sm">{{ note.createdOn | date: 'short' }}</span>
                                    }
                                  </div>
                                  <div class="text-900 white-space-pre-wrap">{{ note.content }}</div>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- Cancelled State -->
                  @if (licenseStore.activeReview()!.currentState === 'Cancelled') {
                    <div class="step-content">
                      <p-message severity="error">
                        <span> This License Renewal has been cancelled. </span>
                      </p-message>
                    </div>
                  }
                </ng-template>
              </p-step-panel>
            </p-step-item>
          </p-stepper>
        </div>
      </div>
    </div>
  }
</app-page>

<!-- Dialogs -->

<!-- Confirm Selection Dialog -->
@if (showConfirmDialogVisible) {
  <p-dialog
    header="Confirm Candidate Selection"
    [(visible)]="showConfirmDialogVisible"
    [modal]="true"
    [draggable]="false"
    [closable]="true"
    [style]="{ width: '500px' }">
    <p-message severity="warn" class="mb-3" [closable]="false">
      <span>
        <strong>Important:</strong> Proceeding will extend licenses and generate new certificates for all
        {{ licenseStore.selectedCandidateIds().length }} selected coaters. GL notification will follow in the next step.
        This action cannot be undone.
      </span>
    </p-message>

    <p class="mb-3">
      @if (licenseStore.unsavedChanges()) {
        <strong>Note:</strong> Your selection changes will be saved automatically before confirmation.
      }
    </p>

    <form [formGroup]="confirmForm" class="field">
      <label for="confirmNotes">Notes (Optional)</label>
      <textarea pTextarea id="confirmNotes" formControlName="notes" rows="3" class="w-full"> </textarea>
    </form>

    <ng-template #footer>
      <p-button label="Cancel" severity="secondary" (onClick)="showConfirmDialogVisible = false"> </p-button>
      <p-button
        label="Confirm and Proceed"
        icon="pi pi-check"
        severity="warn"
        [loading]="licenseStore.activeReviewLoading()"
        (onClick)="confirmSelection()">
      </p-button>
    </ng-template>
  </p-dialog>
}

<!-- Cancel Review Dialog -->
<p-dialog
  header="Cancel License Renewal"
  [visible]="showCancelDialog()"
  (onHide)="showCancelDialog.set(false)"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '500px' }">
  <p-message severity="warn" class="mb-3">
    <span> Warning: Cancelling this review will stop the license renewal process. This action cannot be undone. </span>
  </p-message>

  <form [formGroup]="cancelForm" class="field">
    <label for="cancelReason"> Cancellation Reason <span class="text-red-500">*</span> </label>
    <textarea
      pTextarea
      id="cancelReason"
      formControlName="reason"
      rows="3"
      class="w-full"
      placeholder="Please provide a reason for cancellation">
    </textarea>
    @if (cancelForm.get('reason')?.invalid && cancelForm.get('reason')?.touched) {
      <small class="p-error">Cancellation reason is required</small>
    }
  </form>

  <ng-template #footer>
    <p-button label="Close" severity="secondary" (onClick)="showCancelDialog.set(false)"> </p-button>
    <p-button
      label="Cancel Review"
      icon="pi pi-times"
      severity="danger"
      [disabled]="cancelForm.invalid"
      [loading]="licenseStore.activeReviewLoading()"
      (onClick)="cancelReview()">
    </p-button>
  </ng-template>
</p-dialog>

<p-confirmdialog></p-confirmdialog>
